import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, X, ArrowRight, Star, Shield, Users, Zap, Clock, Target, FileX, Search, AlertTriangle, TrendingDown, Sparkles, BarChart3, Eye, Gauge, Rocket, Award, Brain, Layers, Globe, Briefcase, Trophy, ChevronDown, Play, Pause, Volume2, Settings, Database, UserCheck, Timer, LineChart, Activity, MousePointer, CheckCircle2, XCircle, TrendingUp, Monitor, Smartphone, Tablet, MessageCircle, Quote, Building2, GraduationCap, Lock, FileText, Clipboard, PenTool, Cog, BarChart, Users2, MessageSquare, FileBarChart, History, VideoIcon, ScreenShare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DemoRequestForm } from './DemoRequestForm';
import { FooterSection } from './FooterSection';

// Country codes list with country names
const countryCodes = [{
  code: "+91",
  country: "India"
}, {
  code: "+1",
  country: "United States"
}, {
  code: "+44",
  country: "United Kingdom"
}, {
  code: "+61",
  country: "Australia"
}, {
  code: "+86",
  country: "China"
}, {
  code: "+49",
  country: "Germany"
}, {
  code: "+33",
  country: "France"
}, {
  code: "+81",
  country: "Japan"
}, {
  code: "+7",
  country: "Russia"
}, {
  code: "+55",
  country: "Brazil"
}, {
  code: "+27",
  country: "South Africa"
}, {
  code: "+971",
  country: "UAE"
}, {
  code: "+966",
  country: "Saudi Arabia"
}, {
  code: "+65",
  country: "Singapore"
}, {
  code: "+82",
  country: "South Korea"
}, {
  code: "+39",
  country: "Italy"
}, {
  code: "+34",
  country: "Spain"
}, {
  code: "+52",
  country: "Mexico"
}, {
  code: "+60",
  country: "Malaysia"
}, {
  code: "+64",
  country: "New Zealand"
}, {
  code: "+31",
  country: "Netherlands"
}, {
  code: "+48",
  country: "Poland"
}, {
  code: "+46",
  country: "Sweden"
}, {
  code: "+41",
  country: "Switzerland"
}];

// Process Slider Component
function ProcessSlider() {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const steps = [{
    id: 0,
    title: "Enter Job Description",
    icon: FileText,
    description: "All you need to do is enter or paste your job description. 80% of your work done here.",
    color: "from-green-500 to-green-600",
    bgColor: "from-green-500/20 to-green-500/10",
    accentColor: "text-green-500",
    badgeColor: "bg-green-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Enter%20Job%20Description.gif",
    duration: 9000 // 9 seconds
  }, {
    id: 1,
    title: "Select Question Distribution",
    icon: Settings,
    description: "Select difficulty for each skills type and choose from MCQs, Essay, Audio, and Video answers to evaluate your applicants on a multi-modal platform",
    color: "from-blue-500 to-blue-600",
    bgColor: "from-blue-500/20 to-blue-500/10",
    accentColor: "text-blue-500",
    badgeColor: "bg-blue-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Select%20Question%20Distribution.gif",
    duration: 10800 // 10.8 seconds
  }, {
    id: 2,
    title: "Get AI Questions",
    icon: Brain,
    description: "Testera generates a detailed skills-based assessment for you. Each question is assigned a skill, marks, timer, difficulty, and a marking criteria.",
    color: "from-purple-500 to-purple-600",
    bgColor: "from-purple-500/20 to-purple-500/10",
    accentColor: "text-purple-500",
    badgeColor: "bg-purple-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Get%20AI%20Questions.gif",
    duration: 12000 // 12 seconds
  }, {
    id: 3,
    title: "Customize Questions",
    icon: PenTool,
    description: "Make each question truly yours by getting options to edit every single parameter - skills, marks, timer, difficulty, mark scheme, MCQ options, or even the full question itself.",
    color: "from-orange-500 to-orange-600",
    bgColor: "from-orange-500/20 to-orange-500/10",
    accentColor: "text-orange-500",
    badgeColor: "bg-orange-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Customise%20Questions.gif",
    duration: 11000 // 11 seconds
  }, {
    id: 4,
    title: "Share Test",
    icon: Users2,
    description: "Publish the test and you're all set to receive applications. Share the test with all aspiring applicants and wait for results!",
    color: "from-teal-500 to-teal-600",
    bgColor: "from-teal-500/20 to-teal-500/10",
    accentColor: "text-teal-500",
    badgeColor: "bg-teal-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Share%20Test.gif",
    duration: 8300 // 8.3 seconds
  }, {
    id: 5,
    title: "Receive applications",
    icon: UserCheck,
    description: "Let the candidates give the test in their own time. Testera's advanced proctoring tools monitor the screen for any cheating and unethical behaviour so you don't have to worry about dishonest applicants.",
    color: "from-cyan-500 to-cyan-600",
    bgColor: "from-cyan-500/20 to-cyan-500/10",
    accentColor: "text-cyan-500",
    badgeColor: "bg-cyan-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Receive%20Applications.gif",
    duration: 13000 // 13 seconds
  }, {
    id: 6,
    title: "Review Results",
    icon: BarChart,
    description: "Testera evaluates all submissions for you and gives detailed analytics and feedback for each question. It's time to interview the top scorers!",
    color: "from-indigo-500 to-indigo-600",
    bgColor: "from-indigo-500/20 to-indigo-500/10",
    accentColor: "text-indigo-500",
    badgeColor: "bg-indigo-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Shortlist%20with%20AI%20Scoring.gif",
    duration: 17500 // 17.5 seconds
  }];

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    const gifDuration = steps[activeStep].duration; // Use specific duration for current GIF
    const progressInterval = 50; // Update progress every 50ms
    const totalSteps = gifDuration / progressInterval;
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 100 / totalSteps;
        if (newProgress >= 100) {
          // Move to next step
          setActiveStep(current => (current + 1) % steps.length);
          return 0; // Reset progress
        }
        return newProgress;
      });
    }, progressInterval);
    return () => clearInterval(interval);
  }, [activeStep, isAutoPlaying, steps.length]);

  // Reset progress when manually changing steps
  const handleStepClick = (stepId: number) => {
    setActiveStep(stepId);
    setProgress(0);
    setIsAutoPlaying(false); // Pause auto-play when user manually selects

    // Resume auto-play after 3 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };
  return <div className="space-y-8">
      {/* Main Display Area - Joined Cards */}
      <div className="bg-card rounded-xl shadow-2xl">
        {/* GIF Display Area */}
        <div className="relative bg-gradient-to-br from-muted/30 to-muted/10 p-8 flex items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5"></div>
          
          {/* GIF Display */}
          <div className="relative z-10 w-full">
            <div className="rounded-lg shadow-lg">
              <img src={steps[activeStep].gifUrl} alt={steps[activeStep].title} className="w-full h-auto rounded-lg" />
            </div>
          </div>

          {/* Progress indicator */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <div className="w-32 h-1 bg-muted/50 rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${steps[activeStep].color} transition-all duration-100 ease-linear`} style={{
              width: `${progress}%`
            }} />
            </div>
          </div>
        </div>

        {/* Interactive Step Buttons - Horizontal Scroll for 7 items */}
        <div className="bg-card border-t border-border">
          <div className="flex overflow-x-auto gap-0 scrollbar-hide scroll-smooth" id="steps-container">
            {steps.map((step, index) => <button key={step.id} onClick={() => handleStepClick(step.id)} className={`
                  relative p-4 text-left transition-all duration-300 group min-w-[240px] max-w-[240px] flex-shrink-0
                  ${activeStep === step.id ? 'bg-gradient-to-br from-primary/10 to-secondary/5 border-t-2 border-primary' : 'hover:bg-muted/50 border-t-2 border-transparent hover:border-muted-foreground/20'}
                  ${index !== steps.length - 1 ? 'border-r border-border' : ''}
                `} ref={activeStep === step.id ? el => {
            if (el && !el.dataset.scrolled) {
              el.dataset.scrolled = 'true';
              const container = document.getElementById('steps-container');
              if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = el.getBoundingClientRect();
                const scrollLeft = container.scrollLeft + elementRect.left - containerRect.left - containerRect.width / 2 + elementRect.width / 2;
                container.scrollTo({
                  left: scrollLeft,
                  behavior: 'smooth'
                });
              }
            }
          } : undefined}>
                {/* Active step indicator */}
                {activeStep === step.id && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary animate-pulse" />}

                <div className="flex items-start space-x-3 h-full">
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center relative flex-shrink-0 mt-1
                    transition-all duration-300 group-hover:scale-110 shadow-lg
                    ${activeStep === step.id ? `bg-gradient-to-br ${step.color}` : `bg-gradient-to-br ${step.bgColor}`}
                  `}>
                    {React.createElement(step.icon, {
                  className: `h-4 w-4 ${activeStep === step.id ? 'text-white' : step.accentColor} transition-colors duration-300`
                })}
                    <div className={`absolute -top-1 -right-1 w-4 h-4 ${step.badgeColor} rounded-full flex items-center justify-center text-white text-[10px] font-bold`}>
                      {index + 1}
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-2 min-w-0 py-1">
                    <h3 className={`text-sm font-semibold transition-colors duration-300 leading-tight ${activeStep === step.id ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300 overflow-hidden">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Hover effect indicator */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
              </button>)}
          </div>
        </div>
      </div>
    </div>;
}

// Features Showcase Component
function FeaturesShowcase() {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const features = [{
    name: "Reduce Time to Hire by 75%",
    icon: Clock,
    description: "Instantly screen hundreds of candidates. Focus your time on only the most qualified applicants.",
    category: "Efficiency",
    color: "from-blue-500 to-cyan-500"
  }, {
    name: "Improve Quality of Hire",
    icon: Trophy,
    description: "Job-specific assessments and skills-based evaluation identify top performers before interviews.",
    category: "Quality",
    color: "from-green-500 to-emerald-500"
  }, {
    name: "Scale Your Hiring Process",
    icon: TrendingUp,
    description: "Handle 10x more applications without increasing your recruitment team size.",
    category: "Growth",
    color: "from-purple-500 to-violet-500"
  }, {
    name: "Cut Recruitment Costs",
    icon: Target,
    description: "Reduce cost-per-hire by 60%. No more expensive job boards or recruitment agencies.",
    category: "Cost Savings",
    color: "from-orange-500 to-red-500"
  }, {
    name: "Make Data-Driven Decisions",
    icon: BarChart3,
    description: "Get objective candidate scores and analytics to eliminate hiring bias and improve outcomes.",
    category: "Intelligence",
    color: "from-teal-500 to-cyan-500"
  }, {
    name: "Enhance Candidate Experience",
    icon: Users,
    description: "Modern, mobile-friendly assessments that candidates actually enjoy taking.",
    category: "Experience",
    color: "from-indigo-500 to-blue-500"
  }];
  return <div className="max-w-5xl mx-auto">
      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => <Card key={index} className="relative group cursor-pointer border-2 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden" onMouseEnter={() => setHoveredFeature(index)} onMouseLeave={() => setHoveredFeature(null)}>
            {/* Animated background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-all duration-500`} />
            
            {/* Floating animation dots */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-700">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} />
                <div className="w-2 h-2 bg-secondary/60 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} />
                <div className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} />
              </div>
            </div>

            <CardContent className="p-6 space-y-4 relative z-10">
              <div className="flex items-start space-x-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 bg-gradient-to-br ${feature.color} shadow-lg`}>
                  <feature.icon className="h-7 w-7 text-white drop-shadow-sm" />
                </div>
                
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                      {feature.category}
                    </Badge>
                  </div>
                  
                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors duration-300 text-xl">
                    {feature.name}
                  </h3>
                  
                  <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </div>

              {/* Progress indicator */}
              <div className="relative">
                <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${feature.color} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out`} />
                </div>
              </div>

              {/* Hover effect indicator */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
            </CardContent>
          </Card>)}
      </div>
    </div>;
}
export function NewLandingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Animation trigger on component mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Rotating steps for demo
  const demoSteps = [{
    icon: Zap,
    text: "AI generates questions",
    color: "text-yellow-500"
  }, {
    icon: Users,
    text: "Candidates take assessment",
    color: "text-blue-500"
  }, {
    icon: BarChart3,
    text: "Instant scoring & ranking",
    color: "text-green-500"
  }];
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % demoSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const scrollToForm = () => {
    document.getElementById('early-access-form')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen relative overflow-hidden bg-background">
      {/* Subtle background effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-muted/20 via-transparent to-muted/10" />
        
        {/* Animated gradient orbs with design system colors */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent/15 to-primary/15 rounded-full blur-3xl animate-pulse" style={{
        animationDelay: '2s'
      }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-secondary/10 to-accent/10 rounded-full blur-2xl animate-bounce" style={{
        animationDuration: '8s'
      }} />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="max-w-7xl mx-auto text-center space-y-12">

            {/* Enhanced Main Headline */}
            <div className="space-y-6">
              <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-slate-900 via-primary to-slate-800 bg-clip-text text-transparent leading-tight transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                Hire top talent in{' '}
                <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent animate-pulse">
                  days, not weeks
                </span>
              </h1>
              
              <div className={`text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                AI-powered skill assessments that instantly shortlist your best-fit candidates, cutting hiring time by 80%.
              </div>
            </div>

            {/* Test Creation Prompt Box */}
            <div className={`max-w-2xl mx-auto transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <Card className="border-2 border-primary/20 bg-card/50 backdrop-blur-sm shadow-xl">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="text-left">
                      <Label htmlFor="test-prompt" className="text-sm font-medium text-muted-foreground">
                        Create your assessment in seconds
                      </Label>
                      <Input
                        id="test-prompt"
                        placeholder="Enter job role or description to generate assessment..."
                        className="mt-2 h-12 text-base"
                      />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        variant="default" 
                        className="flex-1 h-12 text-base font-medium"
                      >
                        <PenTool className="h-4 w-4 mr-2" />
                        Manual Test
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="flex-1 h-12 text-base font-medium"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Upload JD
                      </Button>
                      
                      <Button 
                        variant="secondary" 
                        className="flex-1 h-12 text-base font-medium"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Test
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className={`flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <Button size="lg" onClick={scrollToForm} className="w-full sm:w-auto px-8 py-4 text-lg font-semibold bg-orange-500 hover:bg-orange-600 text-white transform hover:scale-105 transition-all duration-200 shadow-2xl hover:shadow-orange-500/25">
                Book a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Button variant="outline" size="lg" className="w-full sm:w-auto px-6 py-4 text-base font-medium border-2 border-primary/30 hover:border-primary hover:bg-primary/5 transform hover:scale-105 transition-all duration-200">
                Generate a Free Test
              </Button>
            </div>

            {/* Social Proof */}
            <div className={`flex flex-col items-center space-y-4 transform transition-all duration-1000 delay-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 text-green-500" />
                <span>Trusted by 500+ companies</span>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />)}
                </div>
                <span>4.9/5 rating</span>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section with Enhanced Visuals */}
        <section className="py-24 px-4 bg-gradient-to-b from-transparent via-muted/30 to-transparent">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-12">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-5xl font-bold text-foreground">
                  Traditional Hiring is 
                  <span className="text-destructive"> Broken</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  You're spending weeks sorting through resumes, only to discover candidates can't do the job. 
                  There has to be a better way.
                </p>
              </div>

              {/* Problem Cards with Enhanced Animations */}
              <div className="grid md:grid-cols-3 gap-8 mt-16">
                {[
                  {
                    icon: Clock,
                    title: "Weeks of Manual Screening",
                    description: "Sorting through hundreds of resumes, scheduling interviews, just to find most candidates are unqualified.",
                    color: "from-red-500 to-orange-500",
                    stat: "3-4 weeks",
                    statLabel: "Average time to hire"
                  },
                  {
                    icon: TrendingDown,
                    title: "Poor Quality Hires",
                    description: "Resumes don't predict performance. 40% of new hires fail within their first 18 months.",
                    color: "from-orange-500 to-red-500",
                    stat: "40%",
                    statLabel: "of hires fail"
                  },
                  {
                    icon: Target,
                    title: "Expensive Mistakes",
                    description: "Bad hires cost 2.5x annual salary. Plus recruitment fees, lost productivity, and team disruption.",
                    color: "from-red-500 to-pink-500",
                    stat: "$75k+",
                    statLabel: "Cost per bad hire"
                  }
                ].map((problem, index) => (
                  <Card key={index} className="relative group border-2 border-destructive/20 hover:border-destructive/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${problem.color} opacity-5 group-hover:opacity-10 transition-all duration-500`} />
                    
                    <CardContent className="p-8 space-y-6 relative z-10">
                      <div className="flex items-center justify-between">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center bg-gradient-to-br ${problem.color} shadow-lg group-hover:scale-110 transition-all duration-300`}>
                          <problem.icon className="h-8 w-8 text-white" />
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-destructive">{problem.stat}</div>
                          <div className="text-sm text-muted-foreground">{problem.statLabel}</div>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold text-foreground group-hover:text-destructive transition-colors duration-300">
                        {problem.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                        {problem.description}
                      </p>

                      <div className="relative">
                        <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${problem.color} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Call-to-action after problems */}
              <div className="mt-16 p-8 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl border border-primary/20">
                <h3 className="text-2xl font-bold text-foreground mb-4">Ready to Fix Your Hiring Process?</h3>
                <p className="text-muted-foreground mb-6">Join hundreds of companies that have transformed their recruitment with skills-based assessments.</p>
                <Button onClick={scrollToForm} size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90">
                  Get Started Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center space-y-6 mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-foreground">
                From Job Description to 
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Perfect Assessment</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our AI analyzes your job requirements and creates a comprehensive skills-based test in under 2 minutes. 
                No technical knowledge required.
              </p>
            </div>

            <ProcessSlider />
          </div>
        </section>

        {/* Enhanced Features Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-muted/20 via-transparent to-muted/20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center space-y-6 mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-foreground">
                Why Companies Choose 
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Testera</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform your hiring process with AI-powered assessments that actually predict job performance.
              </p>
            </div>

            <FeaturesShowcase />
          </div>
        </section>

        {/* Enhanced Social Proof Section */}
        <section className="py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Trusted by Leading Companies
              </h2>
              
              {/* Testimonials Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    name: "Sarah Chen",
                    role: "Head of Talent, TechStart",
                    company: "Series B Startup",
                    testimonial: "Testera reduced our time-to-hire from 6 weeks to 10 days. The quality of candidates has improved dramatically.",
                    rating: 5,
                    avatar: "SC",
                    color: "from-blue-500 to-cyan-500"
                  },
                  {
                    name: "Michael Rodriguez",
                    role: "Recruitment Manager",
                    company: "Global Manufacturing",
                    testimonial: "We've hired 50+ engineers using Testera. 92% are still with us after 12 months vs 60% before.",
                    rating: 5,
                    avatar: "MR",
                    color: "from-green-500 to-emerald-500"
                  },
                  {
                    name: "Emma Thompson",
                    role: "HR Director",
                    company: "Financial Services",
                    testimonial: "The AI-powered scoring eliminated our unconscious bias. Our diversity metrics improved by 40%.",
                    rating: 5,
                    avatar: "ET",
                    color: "from-purple-500 to-violet-500"
                  }
                ].map((testimonial, index) => (
                  <Card key={index} className="relative group border-2 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.color} opacity-0 group-hover:opacity-5 transition-all duration-500`} />
                    
                    <CardContent className="p-6 space-y-4 relative z-10">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                          {testimonial.avatar}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                          <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                          <p className="text-xs text-muted-foreground">{testimonial.company}</p>
                        </div>
                        <div className="flex space-x-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                          ))}
                        </div>
                      </div>
                      
              <blockquote className="text-foreground italic leading-relaxed">
                "{testimonial.testimonial}"
              </blockquote>

              <div className="relative">
                <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${testimonial.color} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out`} />
                </div>
              </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
                {[
                  { stat: "500+", label: "Companies Trust Us" },
                  { stat: "10,000+", label: "Assessments Created" },
                  { stat: "75%", label: "Faster Hiring" },
                  { stat: "4.9/5", label: "User Rating" }
                ].map((stat, index) => (
                  <div key={index} className="text-center space-y-2">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                      {stat.stat}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Early Access Form */}
        <section id="early-access-form" className="py-24 px-4 bg-gradient-to-b from-primary/5 via-secondary/5 to-primary/5">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-primary/20 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-8 border-b border-primary/20">
                <div className="text-center space-y-4">
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                    Ready to Transform Your Hiring?
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    Join hundreds of companies using AI-powered assessments to hire better candidates faster. 
                    Start your free trial today.
                  </p>
                </div>
              </div>
              
              <CardContent className="p-8">
                <DemoRequestForm />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <FooterSection />
      </div>
    </div>;
}